import { CompanyIncorporationEvent } from '../../../../types/events.types';
import { toBackendDateFormat } from '../../../../utils/to-backend-date-format.util';
import { FormSchema } from '../event-form.validation';
import { mapReverseVestingFormToDto, mapReverseVestingToForm } from './reverse-vesting-form.util';

type CompanyIncorporationFormFile = NonNullable<FormSchema['companyIncorporation']>['files'];

export type CompanyIncorporationFormFileEntry = NonNullable<CompanyIncorporationFormFile>[number];

export const resolveCompanyIncorporationManualFileLinks = (
  files: CompanyIncorporationFormFile | undefined,
): string[] =>
  Array.from(
    new Set(
      (files ?? [])
        .map((file) => ('docLink' in file ? file.docLink : undefined))
        .filter((docLink): docLink is string => Boolean(docLink)),
    ),
  );

export const mapCompanyIncorporationEventToForm = (
  event: CompanyIncorporationEvent,
  mapFileLinksToFormFiles: (links: string[]) => CompanyIncorporationFormFileEntry[],
): FormSchema['companyIncorporation'] => ({
  incorporationDate: event.date,
  parValue: String(event.parValue ?? ''),
  issuances: event.items.map(
    ({ id, stakeholder, shareClass, sharesCount, reverseVesting, filesLinks }) => ({
      id,
      stakeholder,
      shareClass,
      shares: sharesCount,
      initialStakeholderId: stakeholder.id,
      reverseVesting: mapReverseVestingToForm(reverseVesting),
      files: mapFileLinksToFormFiles(filesLinks ?? []),
    }),
  ),
  files: mapFileLinksToFormFiles(event.filesLinks ?? []),
  additionalNotes: event.additionalNotes ?? '',
});

export const mapCompanyIncorporationFormToCreateDto = (
  companyIncorporation: NonNullable<FormSchema['companyIncorporation']>,
) => ({
  date: toBackendDateFormat(companyIncorporation.incorporationDate),
  parValue: companyIncorporation.parValue,
  items: companyIncorporation.issuances.map(
    ({ stakeholder, shareClass, shares, reverseVesting, files }) => ({
      stakeholderId: stakeholder.id,
      shareClassId: shareClass.id,
      sharesCount: Number(shares),
      reverseVesting: mapReverseVestingFormToDto(reverseVesting),
      filesLinks: resolveCompanyIncorporationManualFileLinks(files),
    }),
  ),
});

export const mapCompanyIncorporationFormToUpdateDto = (
  companyIncorporation: NonNullable<FormSchema['companyIncorporation']>,
) => ({
  date: toBackendDateFormat(companyIncorporation.incorporationDate),
  parValue: companyIncorporation.parValue,
  items: companyIncorporation.issuances.map(
    ({ id, stakeholder, shareClass, shares, reverseVesting, files }) => ({
      stakeholderId: stakeholder.id,
      shareClassId: shareClass.id,
      sharesCount: Number(shares),
      reverseVesting: mapReverseVestingFormToDto(reverseVesting),
      filesLinks: resolveCompanyIncorporationManualFileLinks(files),
      ...(id ? { id } : {}),
    }),
  ),
});

export const getCompanyIncorporationFileName = (file: unknown): string => {
  if (!file || typeof file !== 'object') return '';

  if ('doc' in file && file.doc && typeof file.doc === 'object' && 'name' in file.doc) {
    return String(file.doc.name);
  }

  return '';
};
